import { cn } from '@/lib/utils';

type EyebrowTone = 'dark' | 'light';

interface EyebrowProps {
  children: React.ReactNode;
  /** Ground the label sits on: `dark` = #121212-ish sections, `light` = #f2f2f2 / white. */
  tone?: EyebrowTone;
  /** Short hairline before the label. Off for labels that sit inside a sentence-like row. */
  rule?: boolean;
  as?: 'p' | 'span' | 'div';
  className?: string;
  id?: string;
}

/**
 * The site's one section label ("eyebrow").
 *
 * Replaces the pill-with-a-green-dot, the pulsing dot and the wide-tracked
 * all-caps kickers, which read as template/AI output. Editorial instead:
 * sentence case Geist (never the mono caps face), muted ink, a short
 * hairline in the label's own colour, no outline, fill or glow. Size and
 * weight stay well below the heading so the hierarchy comes from contrast,
 * not decoration.
 *
 * Contrast: dark tone ≈ 7.5:1 on #121212, light tone ≈ 6:1 on #f2f2f2.
 */
export function Eyebrow({
  children,
  tone = 'dark',
  rule = true,
  as: Tag = 'p',
  className,
  id,
}: EyebrowProps) {
  return (
    <Tag
      id={id}
      className={cn(
        'flex items-center gap-3 text-[14px] font-normal leading-[1.3] tracking-[-0.01em] md:text-[15px]',
        tone === 'dark' ? 'text-foreground/65' : 'text-[#5c5c5c]',
        className,
      )}
    >
      {rule && <span aria-hidden="true" className="h-px w-6 shrink-0 bg-current opacity-60" />}
      <span>{children}</span>
    </Tag>
  );
}
